import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../api";
import {
    DataParams,
    GameLanguages,
    NavigationParams,
    PlainDetails,
    Session,
    Version,
    VersionSection
} from "../../types/admin";
import {languageAdded} from "./modals/gameEditModal";
import {format} from "date-fns";
import {downloadBlob} from "../../components/admin/utils";
import {toastSuccess} from "./admin";

export type AdminGameViewState = {
    loaded: boolean
    requestIds: Record<string, string>
    error: string | null
    game_id: string
    version_id?: string
    current_version_id: string
    draft_id: string
    name: string
    average_duration: string;
    average_points: number;
    created: string;
    modified: string;
    max_points: number;
    min_points: number;
    pages: number;
    played: number;
    languages: GameLanguages[];
    version_sections: VersionSection[];
    active_languages: string[];
    latest_sessions: Session[];
    activeTab: string;
    sections: number;
    slides: number;
    questions: number;
    versions: Version[]
    is_published: boolean;
    is_draft: boolean;
    is_archived: boolean;
    restrict_playing: boolean;
    display_correct_answers: boolean;
    teams: PlainDetails[];
    publish_languages: string[];
    available_for_publish: string[];
    publish_error: string | null;
    publishOpen: boolean;
    preview_url: string;
    statistics_url: string;
}

const initialState: AdminGameViewState = {
    loaded: false,
    requestIds: {},
    error: null,
    game_id: '',
    current_version_id: '',
    draft_id: '',
    name: '',
    average_duration: '',
    average_points: 0,
    created: '',
    modified: '',
    max_points: 0,
    min_points: 0,
    pages: 0,
    played: 0,
    languages: [],
    version_sections: [],
    active_languages: [],
    latest_sessions: [],
    activeTab: 'default',
    sections: 0,
    slides: 0,
    questions: 0,
    versions: [],
    is_published: false,
    is_draft: false,
    is_archived: false,
    restrict_playing: false,
    display_correct_answers: true,
    teams: [],
    publish_languages: [],
    publish_error: null,
    available_for_publish: [],
    publishOpen: false,
    preview_url: '',
    statistics_url: ''
}

export type GameDataParams = {
    game_id: string
    version_id: string
}

type OnlyGameIdParams = {
    game_id: string
}

type CombinedParams = OnlyGameIdParams & DataParams;


export const getAdminGameDetail = createAsyncThunk(
    'adminGameView/getAdminGameDetail',
    async (params: GameDataParams, thunkAPI) => {
        try {
            return await api.get(`/API/admin/games/${params.game_id}/versions/${params.version_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const extractParams = (params: CombinedParams): NavigationParams => {
    let _params: any = {};
    if (params.sq) _params.search = params.sq;
    if (params.team) _params.team = params.team;
    if (params.passed) _params.passed = params.passed;
    return _params;
}


export const addDraft = createAsyncThunk(
    'adminGameView/addDraft',
    async (params: GameDataParams, thunkAPI) => {
        try {
            return await api.post(`/API/admin/games/${params.game_id}/versions/${params.version_id}/create-draft/`, params);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const getGameSessionExcel = createAsyncThunk(
    'adminGameView/getExcel',
    async (params: CombinedParams, thunkAPI) => {
        const _params = extractParams(params);
        try {
            return await api.getExcel(`/API/admin/games/${params.game_id}/sessions/export/`, _params);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


type addGameLanguageParams = {
    game_id: string
    version_id: string
    language_code: string
}

export const addGameLanguage = createAsyncThunk(
    'adminGameView/addGameLanguage',
    async (payload: addGameLanguageParams, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        try {
            const response = await api.post(`/API/admin/games/${payload.game_id}/versions/${payload.version_id}/languages/`, payload);
            dispatch(languageAdded({
                ...response,
                language: payload.language_code
            }));
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const duplicateGame = createAsyncThunk(
    'adminGameView/duplicateGame',
    async (params: GameDataParams, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.post(`/API/admin/games/${params.game_id}/versions/${params.version_id}/duplicate/`, params);
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type UpdateSectionOrderParams = {
    game_id: string
    version_id: string
    version_sections: VersionSection[]
}

export const updateSectionOrder = createAsyncThunk(
    'adminGameView/updateSectionOrder',
    async (payload: UpdateSectionOrderParams, thunkAPI) => {
        try {
            return await api.put(`/API/admin/games/${payload.game_id}/versions/${payload.version_id}/sections/`, payload);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const publishVersionPromise = createAsyncThunk(
    'adminGameView/publishVersionPromise',
    async (payload: { game_id: string, version_id: string }, thunkAPI) => {
        const {game_id, version_id} = payload;
        try {
            return await api.get(`/API/admin/games/${game_id}/versions/${version_id}/publish/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export const publishVersion = createAsyncThunk(
    'adminGameView/publishVersion',
    async (payload: { game_id: string, version_id: string, publish_languages: string[] }, thunkAPI) => {
        const {game_id, version_id} = payload;
        try {
            return await api.post(`/API/admin/games/${game_id}/versions/${version_id}/publish/`, payload);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


const slice = createSlice({
    name: 'adminGameView',
    initialState,
    reducers: {
        deleteLatestSession: (state, action) => {
            if (action.payload.session_id) {
                state.latest_sessions = state.latest_sessions.filter(session => session.session_id !== action.payload.session_id);
            }
        },
        closePublishModal: (state) => {
            state.publishOpen = false;
        },
        changePublishLanguages: (state, action) => {
            state.publish_languages = action.payload;
        },
        changeTab: (state, action) => {
            state.activeTab = action.payload;
        },
        addSection: (state, action) => {
            state.version_sections.push(action.payload);
        },
        updateSection: (state, action) => {
            const section = state.version_sections.find(section => section.section_id === action.payload.section_id);
            if (section) {
                Object.assign(section, action.payload);
            }
        },
        changeGameDetail: (state, action: PayloadAction<Partial<AdminGameViewState>>) => {
            return {
                ...state,
                ...action.payload
            };
        },

    },
    extraReducers: (builder) => {
        builder
            .addCase(getGameSessionExcel.pending, (state) => {
                state.loaded = false;
            })
            .addCase(getGameSessionExcel.fulfilled, (state, action) => {
                state.loaded = true;
                const formattedDate = format(new Date(), 'ddMMyyyy');
                const fileName = `sessions-${formattedDate}.xlsx`;
                downloadBlob(action.payload, fileName);
            })
            .addCase(getGameSessionExcel.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(publishVersion.pending, (state) => {
                state.loaded = false;
            })
            .addCase(publishVersion.fulfilled, (state) => {
                state.loaded = true;
                state.publishOpen = false;
                state.publish_error = null;
            })
            .addCase(publishVersion.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(publishVersionPromise.pending, (state) => {
                state.loaded = false;
                state.error = null;
                state.publish_error = null;
            })
            .addCase(publishVersionPromise.fulfilled, (state, action) => {
                state.loaded = true;
                state.publishOpen = true;
                state.publish_error = action.payload.publish_error;
                state.available_for_publish = action.payload.available_for_publish;
                state.publish_languages = action.payload.publish_languages;
            })
            .addCase(publishVersionPromise.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
                        // Page-level slices have no isOpen, so guard on the request id alone: a slow
            // response for one entity must not overwrite the one the user navigated to.
            .addCase(addGameLanguage.pending, (state, action) => {
                state.requestIds.addGameLanguage = action.meta.requestId;
                state.loaded = false;
            })
            .addCase(addGameLanguage.fulfilled, (state, action) => {
                if (state.requestIds.addGameLanguage !== action.meta.requestId) return;
                Object.assign(state, action.payload);
                state.activeTab = 'default';
                state.loaded = true;
            })
            .addCase(addGameLanguage.rejected, (state, action) => {
                if (state.requestIds.addGameLanguage !== action.meta.requestId) return;
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
                        // Page-level slices have no isOpen, so guard on the request id alone: a slow
            // response for one entity must not overwrite the one the user navigated to.
            .addCase(getAdminGameDetail.pending, (state, action) => {
                state.requestIds.getAdminGameDetail = action.meta.requestId;
                state.loaded = false;
            })
            .addCase(getAdminGameDetail.fulfilled, (state, action) => {
                if (state.requestIds.getAdminGameDetail !== action.meta.requestId) return;
                state.loaded = true;
                Object.assign(state, action.payload);
            })
            .addCase(getAdminGameDetail.rejected, (state, action) => {
                if (state.requestIds.getAdminGameDetail !== action.meta.requestId) return;
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(duplicateGame.pending, (state) => {
                state.loaded = false;
            })
            .addCase(duplicateGame.fulfilled, (state) => {
                state.loaded = true;
            })
            .addCase(duplicateGame.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(addDraft.pending, (state) => {
                state.loaded = false;
            })
            .addCase(addDraft.fulfilled, (state) => {
                state.loaded = false;
            })
            .addCase(addDraft.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(updateSectionOrder.pending, (state) => {
                state.loaded = false;
            })
            .addCase(updateSectionOrder.fulfilled, (state, action) => {
                state.loaded = true;
                state.version_sections = action.payload.version_sections;
            })
            .addCase(updateSectionOrder.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            });
    }
});

export const {
    deleteLatestSession,
    changePublishLanguages,
    changeGameDetail,
    updateSection,
    addSection,
    changeTab,
    closePublishModal
} = slice.actions;
export default slice.reducer;